import { Box } from '@material-ui/core';
import { Currency, getMoneySymbolByCurrency } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import z from 'zod';

import { ApplicationStepContainer } from '@/components/modules/Application/components/_shared/components/ApplicationStepContainer';
import { ApplicationStepNav } from '@/components/modules/Application/components/_shared/components/ApplicationStepNav';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { SectionTitle } from '@/components/modules/Application/components/_shared/components/Section/SectionTitle';
import { CartTotal } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartForm/components/CartTotal';
import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { KtuinProductIcon } from '@/components/modules/Application/components/Cart/KtuinCart/components/KtuinProductIcon';
import { useKtuinCart } from '@/components/modules/Application/components/Cart/KtuinCart/hooks/useKtuinCart';
import { KtuinProductType } from '@/components/modules/Application/components/Cart/KtuinCart/types/KtuinProductType';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { GetTextIdTranslationType, useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';
import { SelectOption } from '@/types/selectOption';
import { numberToString, requiredStringToNumber } from '@/utils/zodUtils';

const productTypes: SelectOption[] = Object.values(KtuinProductType).map((value) => ({
  value,
  display: (
    <Box display="flex" alignItems="center" gridGap={8}>
      <KtuinProductIcon type={value} size={20} />
      {value}
    </Box>
  ),
}));

export const KtuinProductForm = () => {
  const { cart } = useCart();

  const defaultValues = useMemo(() => {
    return getDefaultValues(cart);
  }, [cart]);

  return (
    <FormWrapper formSchema={FormSchema} defaultValues={defaultValues}>
      <Form />
    </FormWrapper>
  );
};

export const Form = () => {
  const { onNext } = useApplicationNavigation();

  const { isMobile } = useScreen();
  const { getTextIdTranslation } = useTranslation();

  const form = useFormContext<FormFields>();

  const type = form.watch('type');
  const price = form.watch('price');
  const description = form.watch('description');
  const resetDescription = () => form.resetField('description');

  const { cart, paymentMethodsAvailable, submitCart } = useKtuinCart({
    type,
    price,
    description,
    resetDescription,
  });

  const onSubmit = () => {
    submitCart();
    onNext();
  };

  return (
    <ApplicationStepContainer>
      <Section>
        <SectionTitle title={'CART'} subtitle={'CART_TEXT_KTUIN'} />
        <CustomPaper fill="paper">
          <FormGridContainer direction="row" fieldVariant={FieldVariant.form}>
            <SelectFormField name={'type'} label={'PRODUCT'} selectOptions={productTypes} />
            {type === KtuinProductType.OTHERS && (
              <Box height={isMobile ? 'auto' : 52}>
                <TextFormField name={'description'} label={'DESCRIPTION'} />
              </Box>
            )}
            <NumberFormField
              name={'price'}
              valueUnit={getMoneySymbolByCurrency(cart?.currency ?? Currency.EUR)}
              label={'TOTAL_TO_FINANCE'}
              style={{ textAlign: 'right' }}
            />
          </FormGridContainer>
          <Box mt={6}>
            <CartTotal cartTotal={cart?.total() ?? 0} currency={cart?.currency ?? Currency.EUR} />
          </Box>
        </CustomPaper>
      </Section>
      <ApplicationStepNav
        onNext={form.handleSubmit(() => onSubmit())}
        // disableOnNext={!paymentMethodsAvailable}
        tooltipOnNextDisabled={
          !paymentMethodsAvailable
            ? getTextIdTranslation('NO_PAYMENT_METHODS_AVAILABLE_FOR_AMOUNT')
            : getTextIdTranslation('')
        }
      />
    </ApplicationStepContainer>
  );
};

const FormSchema = (t: GetTextIdTranslationType) =>
  z
    .object({
      type: z.nativeEnum(KtuinProductType, {
        errorMap: () => ({ message: t('REQUIRED_FIELD') }),
      }),
      description: z.string().optional(),
      price: requiredStringToNumber(t)
        .refine((val) => val !== 0, { message: t('AMOUNT_NOT_ZERO') })
        .transform(numberToString),
    })
    .refine((data) => data.type !== KtuinProductType.OTHERS || data.description, {
      message: t('REQUIRED_FIELD'),
      path: ['description'],
    });

type FormFields = z.infer<ReturnType<typeof FormSchema>>;

const getDefaultValues = (cart?: CartEntity) => {
  if (!cart || cart.items.length === 0) {
    return { type: '' as KtuinProductType, price: '' };
  }

  const item = cart.items.map((item) => item.toFormItem())[0];

  if (Object.values(KtuinProductType).includes(item.description as KtuinProductType)) {
    return { type: item.description! as KtuinProductType, price: item.price! };
  }

  return { type: KtuinProductType.OTHERS, price: item.price!, description: item.description! };
};
